/**
 * UI primitives.
 *
 * Ported from the Firehorse design kit in fh-vai-agent-frontend, with the three
 * gaps that app left open filled in here rather than hand-rolled per page: it has
 * 48 bespoke modals and no table primitive, and this app has far more of both.
 *
 * Conventions: variants are literal class maps (see pillTones.ts for why),
 * structure is drawn with inset hairline shadows, and every interactive element
 * carries `.fh-focus`.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { FiAlertCircle, FiCheck, FiChevronDown, FiChevronUp, FiSearch, FiX } from "react-icons/fi";
import { LuLoaderCircle } from "react-icons/lu";
import { cn, initials } from "@/lib/utils";
import { BADGE_TONE, type Tone, type ToneMap } from "./pillTones";

// =====================================================================
// Button
// =====================================================================

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md";

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-fh-navy-deep text-white hover:bg-fh-navy disabled:opacity-60",
  secondary:
    "bg-white text-fh-text-strong shadow-[inset_0_0_0_1px_var(--color-fh-border-button)] hover:border-fh-navy hover:bg-fh-surface-soft",
  ghost: "bg-transparent text-fh-text-secondary hover:bg-fh-surface-soft hover:text-fh-text-mid",
  danger: "bg-fh-danger text-white hover:opacity-90",
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-fh-cell rounded-fh-input",
  md: "px-4 py-2 text-fh-row rounded-fh-button",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
}

export function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  icon,
  iconPosition = "left",
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || isLoading}
      className={cn(
        "fh-focus inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 font-medium transition-colors disabled:cursor-not-allowed",
        BUTTON_VARIANT[variant],
        BUTTON_SIZE[size],
        variant === "primary" && "shadow-fh-button",
        className,
      )}
      {...rest}
    >
      {isLoading ? (
        <LuLoaderCircle className="animate-spin" aria-hidden />
      ) : (
        iconPosition === "left" && icon
      )}
      {children}
      {!isLoading && iconPosition === "right" && icon}
    </button>
  );
}

/** Square icon-only button, for row actions and panel headers. */
export function IconButton({
  className,
  children,
  label,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        "fh-focus inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-fh-input text-fh-text-secondary shadow-fh-hairline transition-colors hover:bg-fh-surface-soft hover:text-fh-text-mid disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

// =====================================================================
// Card + StatCard
// =====================================================================

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        // border-0 + inset hairline: a real border would double up against the
        // shadow on adjacent cards.
        "rounded-fh-panel border-0 bg-white shadow-fh-hairline-soft",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
      <div className="min-w-0">
        <h2 className="text-fh-panel-title font-medium text-fh-text-title">{title}</h2>
        {subtitle && <p className="mt-1 text-fh-body text-fh-text-secondary">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("px-5 pb-5", className)}>{children}</div>;
}

export function StatCard({
  label,
  value,
  icon,
  delta,
  deltaTone = "neutral",
  onClick,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  delta?: string;
  deltaTone?: "positive" | "negative" | "neutral";
  onClick?: () => void;
}) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn(
        "flex flex-col gap-3 rounded-fh-stat border-0 bg-white px-5 py-5 text-left shadow-fh-hairline-soft",
        onClick && "fh-focus cursor-pointer transition-shadow hover:shadow-fh-tab",
      )}
    >
      <div className="flex items-center gap-2 text-fh-text-body">
        {icon && <span className="text-lg">{icon}</span>}
        <span className="text-fh-body font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <p className="fh-numeric text-fh-stat font-medium text-fh-text-strong">{value}</p>
        {delta && (
          <span
            className={cn(
              "text-fh-xs font-medium",
              deltaTone === "positive" && "text-fh-positive",
              deltaTone === "negative" && "text-fh-danger",
              deltaTone === "neutral" && "text-fh-text-secondary",
            )}
          >
            {delta}
          </span>
        )}
      </div>
    </Wrapper>
  );
}

// =====================================================================
// PillBadge
// =====================================================================

export function PillBadge({
  value,
  tone,
  toneMap,
  shape = "badge",
  showDot = false,
  className,
}: {
  value: ReactNode;
  tone?: Tone;
  /** Resolve the tone from a map keyed by the value. Falls back to neutral. */
  toneMap?: ToneMap;
  shape?: "badge" | "pill";
  showDot?: boolean;
  className?: string;
}) {
  const resolved =
    tone ?? (toneMap && typeof value === "string" ? toneMap[value] : undefined) ?? BADGE_TONE.neutral;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 text-fh-cell font-medium whitespace-nowrap",
        shape === "pill" ? "rounded-full" : "rounded-fh-badge",
        resolved.fg,
        resolved.bg,
        resolved.stroke,
        className,
      )}
    >
      {showDot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", resolved.dot)} />}
      {value || "—"}
    </span>
  );
}

// =====================================================================
// Avatar
// =====================================================================

export function Avatar({
  fullName,
  size = "md",
  className,
}: {
  fullName: string;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <span
      className={cn(
        // The kit's muted variant, not the source app's 10-colour hash ramp: this
        // palette reserves colour for status meaning.
        "inline-flex shrink-0 items-center justify-center rounded-full bg-fh-surface-avatar font-medium text-fh-text-mid",
        size === "sm" ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-[12px]",
        className,
      )}
      title={fullName}
    >
      {initials(fullName)}
    </span>
  );
}

// =====================================================================
// Form controls
// =====================================================================

export function FieldLabel({
  htmlFor,
  children,
  required,
  hint,
}: {
  htmlFor?: string;
  children: ReactNode;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label htmlFor={htmlFor} className="block text-fh-body font-medium text-fh-text-mid">
      {children}
      {required && <span className="ml-0.5 text-fh-danger">*</span>}
      {hint && <span className="ml-2 font-normal text-fh-text-secondary">{hint}</span>}
    </label>
  );
}

export function FieldError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p className="mt-1 flex items-center gap-1 text-fh-xs text-fh-danger">
      <FiAlertCircle aria-hidden /> {children}
    </p>
  );
}

const CONTROL_BASE =
  "fh-focus w-full rounded-fh-input bg-white px-3 py-2 text-fh-row text-fh-text-strong placeholder:text-fh-text-placeholder shadow-[inset_0_0_0_1px_var(--color-fh-border-input)] transition-shadow disabled:cursor-not-allowed disabled:bg-fh-surface-soft disabled:text-fh-text-secondary";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

export function Input({ label, error, hint, required, className, id, ...rest }: InputProps) {
  const inputId = id ?? rest.name;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <FieldLabel htmlFor={inputId} required={required} hint={hint}>
          {label}
        </FieldLabel>
      )}
      <input
        id={inputId}
        aria-invalid={Boolean(error)}
        className={cn(
          CONTROL_BASE,
          error && "shadow-[inset_0_0_0_1px_var(--color-fh-danger)]",
          className,
        )}
        {...rest}
      />
      <FieldError>{error}</FieldError>
    </div>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

export function Textarea({ label, error, hint, required, className, id, ...rest }: TextareaProps) {
  const inputId = id ?? rest.name;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <FieldLabel htmlFor={inputId} required={required} hint={hint}>
          {label}
        </FieldLabel>
      )}
      <textarea
        id={inputId}
        rows={rest.rows ?? 4}
        aria-invalid={Boolean(error)}
        className={cn(
          CONTROL_BASE,
          "resize-y",
          error && "shadow-[inset_0_0_0_1px_var(--color-fh-danger)]",
          className,
        )}
        {...rest}
      />
      <FieldError>{error}</FieldError>
    </div>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export function Select({
  label,
  error,
  hint,
  required,
  options,
  placeholder,
  className,
  id,
  ...rest
}: SelectProps) {
  const inputId = id ?? rest.name;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <FieldLabel htmlFor={inputId} required={required} hint={hint}>
          {label}
        </FieldLabel>
      )}
      <select
        id={inputId}
        aria-invalid={Boolean(error)}
        className={cn(
          CONTROL_BASE,
          "cursor-pointer appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23808080%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:16px] bg-[right_0.65rem_center] bg-no-repeat pr-9",
          error && "shadow-[inset_0_0_0_1px_var(--color-fh-danger)]",
          className,
        )}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldError>{error}</FieldError>
    </div>
  );
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cn("flex cursor-pointer items-center gap-2 text-fh-row", className)}>
      <input
        type="checkbox"
        className="fh-focus h-4 w-4 cursor-pointer rounded-[4px] accent-fh-navy"
        {...rest}
      />
      <span className="text-fh-text-mid">{label}</span>
    </label>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = "Search",
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative", className)}>
      <FiSearch
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fh-text-secondary"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={cn(CONTROL_BASE, "pl-9")}
      />
    </div>
  );
}

// =====================================================================
// Tabs
// =====================================================================

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: { id: T; label: string; count?: number }[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex max-w-full overflow-x-auto rounded-fh-button bg-fh-surface-sidebar p-1 no-scrollbar",
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={cn(
              "fh-focus flex cursor-pointer items-center gap-1.5 rounded-fh-input px-4 py-1.5 text-fh-row whitespace-nowrap transition-colors",
              isActive
                ? "bg-white font-medium text-fh-text-strong shadow-fh-tab"
                : "text-fh-text-secondary hover:text-fh-text-mid",
            )}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span
                className={cn(
                  "rounded-full px-1.5 text-fh-meta font-medium",
                  isActive
                    ? "bg-fh-navy text-white"
                    : "bg-fh-badge-neutral-bg text-fh-badge-neutral-text",
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// =====================================================================
// Skeleton / Empty / Spinner
// =====================================================================

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-fh-input bg-fh-surface-sidebar", className)} />;
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-16 rounded-fh-panel" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-fh-panel bg-fh-surface-faint px-6 py-14 text-center shadow-fh-hairline-soft">
      {icon && <span className="mb-1 text-2xl text-fh-text-muted">{icon}</span>}
      <p className="text-fh-detail font-medium text-fh-text-mid">{title}</p>
      {body && <p className="max-w-md text-fh-body text-fh-text-secondary">{body}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <LuLoaderCircle className={cn("animate-spin text-fh-navy", className)} aria-hidden />;
}

// =====================================================================
// Modal
// =====================================================================

/**
 * One modal primitive. The source app hand-rolled 48 of these; each one is a
 * chance to forget the focus trap or the body scroll lock.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  footer,
  width = 576,
  flush = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  width?: number;
  /**
   * Full-bleed body at a fixed 90vh, for content that manages its own scrolling
   * and wants every pixel — the PDF viewer's iframe. Ordinary modals size to their
   * content and scroll the body, which is the default.
   */
  flush?: boolean;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * onClose in a ref, and NOT an effect dependency.
   *
   * This was the "I type one character and have to click the field again" bug. Every
   * call site passes an inline arrow (`onClose={() => setOpen(false)}`), so its identity
   * changes on every render of the parent. Each keystroke re-rendered the parent, which
   * re-ran this effect: the cleanup restored focus to whatever had it when the effect
   * last ran, then the body called `panelRef.current.focus()` and the dialog container
   * took focus off the input. One character per click, in every modal in the app.
   *
   * Depending only on `open` means the trap is installed once per opening, which is what
   * was always intended — the listener reads onCloseRef, so it still calls the current
   * handler.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      // Focus trap: without it, tabbing walks into the page behind the backdrop.
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    // Focus the panel so a screen reader announces the dialog rather than leaving
    // the caret wherever it was.
    panelRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]"
      onMouseDown={(event) => {
        // Only a click on the backdrop itself closes — not a drag that ends there.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ maxWidth: width }}
        className={cn(
          "flex w-full flex-col overflow-hidden rounded-fh-panel bg-white text-fh-text-strong shadow-fh-panel outline-none",
          flush ? "h-[90vh]" : "max-h-[90vh]",
        )}
      >
        <div className="flex items-start justify-between gap-4 p-5">
          <div className="min-w-0">
            <h2 className="text-fh-modal-title font-medium text-fh-text-title">{title}</h2>
            {subtitle && <p className="mt-1 text-fh-body text-fh-text-secondary">{subtitle}</p>}
          </div>
          <IconButton label="Close" onClick={onClose}>
            <FiX aria-hidden />
          </IconButton>
        </div>

        <div
          className={cn(
            "flex-1",
            flush ? "min-h-0 overflow-hidden" : "overflow-y-auto px-5 pb-5",
          )}
        >
          {children}
        </div>

        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-fh-border-soft p-5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation prompt, for destructive or irreversible actions. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = "Confirm",
  variant = "primary",
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  variant?: ButtonVariant;
  isLoading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={460}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant={variant} size="sm" onClick={onConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-fh-detail text-fh-text-body">{body}</div>
    </Modal>
  );
}

// =====================================================================
// SidePanel
// =====================================================================

/**
 * The kit's 540px detail sheet. It PUSHES the list rather than overlaying it, so
 * the row you came from stays visible and in context.
 */
export function SidePanel({
  open,
  onClose,
  title,
  subtitle,
  actions,
  children,
  width = 540,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  width?: number;
}) {
  if (!open) return null;
  return (
    <aside
      style={{ width }}
      className="m-5 flex h-[calc(100vh-40px)] flex-none flex-col overflow-hidden rounded-fh-panel bg-white shadow-fh-panel max-lg:fixed max-lg:inset-0 max-lg:z-[9000] max-lg:m-0 max-lg:h-full max-lg:w-full max-lg:rounded-none"
    >
      <div className="flex items-start justify-between gap-3 border-b border-fh-border-soft p-5">
        <div className="min-w-0">
          <h2 className="truncate text-fh-panel-title font-medium text-fh-text-title">{title}</h2>
          {subtitle && <p className="mt-1 text-fh-body text-fh-text-secondary">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <IconButton label="Close panel" onClick={onClose}>
            <FiX aria-hidden />
          </IconButton>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5">{children}</div>
    </aside>
  );
}

// =====================================================================
// DataTable
// =====================================================================

export interface Column<T> {
  key: string;
  header: string;
  /** CSS grid track, e.g. "minmax(0,1fr)" or "140px". */
  width: string;
  render: (row: T) => ReactNode;
  /** Value used for sorting and text search. Omit to make the column inert. */
  sortValue?: (row: T) => string | number;
  align?: "left" | "right";
}

/**
 * The kit's CSS-grid card-row table, generalised.
 *
 * Rows are cards with a 6px gap, not ruled lines — that is the design kit's idiom
 * and it is why there is no divider anywhere in here.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selectedKey,
  isLoading,
  empty,
  minWidth,
  pageSize = 25,
  searchable = true,
  searchPlaceholder = "Search",
  toolbar,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string;
  isLoading?: boolean;
  empty?: ReactNode;
  minWidth?: number;
  pageSize?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  toolbar?: ReactNode;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const [page, setPage] = useState(0);

  const template = columns.map((column) => column.width).join(" ");

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const needle = query.trim().toLowerCase();
    return rows.filter((row) =>
      columns.some((column) => {
        const value = column.sortValue?.(row);
        return value !== undefined && String(value).toLowerCase().includes(needle);
      }),
    );
  }, [rows, columns, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return filtered;
    // Copy before sorting: mutating the array from a query cache would reorder
    // other consumers of the same data.
    return [...filtered].sort((a, b) => {
      const left = column.sortValue!(a);
      const right = column.sortValue!(b);
      const comparison =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right));
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [filtered, sort, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  // Clamp rather than reset: deleting the last row of page 3 should land on page 2,
  // not page 1.
  const safePage = Math.min(page, pageCount - 1);
  const visible = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const toggleSort = (key: string) => {
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {(searchable || toolbar) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {searchable ? (
            <SearchInput
              value={query}
              onChange={(value) => {
                setQuery(value);
                setPage(0);
              }}
              placeholder={searchPlaceholder}
              className="w-full max-w-xs"
            />
          ) : (
            <div />
          )}
          {toolbar}
        </div>
      )}

      <div className="fh-scroll-x">
        <div style={minWidth ? { minWidth } : undefined}>
          <div
            className="mb-2 grid items-center gap-4 rounded-fh-input bg-fh-surface-table-header px-4 py-2.5 text-fh-th font-medium text-fh-text-secondary"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => (
              <button
                key={column.key}
                type="button"
                disabled={!column.sortValue}
                onClick={() => column.sortValue && toggleSort(column.key)}
                className={cn(
                  "flex items-center gap-1 truncate text-left",
                  column.align === "right" && "justify-end text-right",
                  column.sortValue ? "fh-focus cursor-pointer hover:text-fh-text-mid" : "cursor-default",
                )}
              >
                {column.header}
                {sort?.key === column.key &&
                  (sort.direction === "asc" ? (
                    <FiChevronUp aria-hidden />
                  ) : (
                    <FiChevronDown aria-hidden />
                  ))}
              </button>
            ))}
          </div>

          {isLoading ? (
            <TableSkeleton />
          ) : visible.length === 0 ? (
            (empty ?? <EmptyState title="Nothing to show" />)
          ) : (
            <div className="flex flex-col gap-1.5">
              {visible.map((row) => {
                const key = rowKey(row);
                const isSelected = selectedKey === key;
                return (
                  <div
                    key={key}
                    role={onRowClick ? "button" : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      "grid items-center gap-4 rounded-fh-panel bg-white px-4 py-3 text-fh-cell text-fh-text-mid transition-shadow",
                      onRowClick && "fh-focus cursor-pointer hover:shadow-fh-tab",
                      isSelected ? "shadow-fh-panel" : "shadow-fh-hairline-soft",
                    )}
                    style={{ gridTemplateColumns: template }}
                  >
                    {columns.map((column) => (
                      <div
                        key={column.key}
                        className={cn("min-w-0", column.align === "right" && "text-right")}
                      >
                        {column.render(row)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {!isLoading && sorted.length > pageSize && (
        <div className="flex items-center justify-between text-fh-body text-fh-text-secondary">
          <span>
            {safePage * pageSize + 1}–{Math.min(sorted.length, (safePage + 1) * pageSize)} of{" "}
            {sorted.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(safePage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// MaskedField
// =====================================================================

/**
 * Displays a masked sensitive value with an optional reveal.
 *
 * Reveal is always a server round-trip to a `?reveal=true` endpoint, which writes an
 * audit entry. The plaintext is held in local state only and never enters the redux
 * cache, so it does not persist past this component unmounting.
 */
/**
 * NOTE: currently unused. Bank details are shown in full on request, so nothing masks a
 * value today. Kept because it is self-contained and that decision has already reversed
 * once — if masking returns, it returns here.
 */
export function MaskedField({
  label,
  masked,
  onReveal,
  canReveal = false,
  revealNote,
}: {
  label: string;
  masked: string;
  onReveal?: () => Promise<string>;
  canReveal?: boolean;
  /**
   * Shown once revealed. Optional and unset by default because the note has to be TRUE:
   * this used to always read "this view was logged", which was right for the audited bank
   * reveal and would be a lie for a value that was already in the page and is merely
   * hidden from the screen.
   */
  revealNote?: string;
}) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reveal = async () => {
    if (!onReveal) return;
    setIsLoading(true);
    setError(null);
    try {
      setRevealed(await onReveal());
    } catch (err) {
      setError(err instanceof Error ? err.message : "could not reveal this value");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="text-fh-body text-fh-text-label">{label}</span>
      <div className="flex items-center gap-2">
        <span className="fh-numeric text-fh-detail text-fh-text-value">
          {revealed ?? (masked || "—")}
        </span>
        {canReveal && onReveal && !revealed && masked && (
          <button
            type="button"
            onClick={reveal}
            disabled={isLoading}
            className="fh-focus cursor-pointer text-fh-xs font-medium text-fh-navy hover:underline disabled:opacity-50"
          >
            {isLoading ? "Revealing…" : "Reveal"}
          </button>
        )}
        {revealed && revealNote && (
          <span className="inline-flex items-center gap-1 text-fh-xs text-fh-text-secondary">
            <FiCheck aria-hidden /> {revealNote}
          </span>
        )}
        {revealed && canReveal && (
          <button
            type="button"
            onClick={() => setRevealed(null)}
            className="fh-focus cursor-pointer text-fh-xs font-medium text-fh-navy hover:underline"
          >
            Hide
          </button>
        )}
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
}

// =====================================================================
// DetailList
// =====================================================================

/** Label/value rows, the kit's detail-panel treatment. */
export function DetailList({
  items,
  columns = 1,
}: {
  items: { label: string; value: ReactNode }[];
  columns?: 1 | 2;
}) {
  return (
    <dl className={cn("grid gap-4", columns === 2 ? "sm:grid-cols-2" : "grid-cols-1")}>
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-1">
          <dt className="text-fh-body text-fh-text-label">{item.label}</dt>
          <dd className="text-fh-detail break-words text-fh-text-value">{item.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SectionHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-fh-detail font-medium text-fh-text-strong">{children}</h3>
      {action}
    </div>
  );
}
